import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableRow } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { usePMT3Selection } from '@/context/PMT3SelectionContext';
import { usePmt3LifetimeResultsData } from '@/hooks/usePmt3LifetimeResultsData'; // Import the hook
// ** Import new date utils **
import { parseNumericValue, excelSerialDateToJSDate, formatDateMMDDYYYY } from '@/services/pmt2Utils';
import { cn } from "@/lib/utils"; // Import cn utility

// --- Formatting Helpers ---
const formatCurrency = (value: any, decimals = 0): string => {
    const num = parseNumericValue(value);
    if (num === null) return '-';
    return `$${num.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}`;
};

const formatNumber = (value: any): string => {
    const num = parseNumericValue(value);
    if (num === null) return '-';
    return num.toLocaleString('en-US');
};

// Formats a decimal value (e.g., 0.25) into a rounded percentage string (e.g., "25%")
const formatDecimalAsPercent = (value: any): string => {
    const num = parseNumericValue(value);
    if (num === null) return '-';
    return `${Math.round(num * 100)}%`;
};

// ** Updated date formatter to handle Excel serial **
const formatExcelDate = (value: any): string => {
    const jsDate = excelSerialDateToJSDate(value); // Convert serial to JS Date
    if (!jsDate) return '-'; // Return '-' if conversion fails
    return formatDateMMDDYYYY(jsDate); // Format the JS Date
}
// --- End Formatting Helpers ---


const LifetimeResultsDashboard: React.FC = () => {
    const { selectedCompany, selectedProduct } = usePMT3Selection();
    const { asinData, companyLifetimeRevenue, isLoading, error } = usePmt3LifetimeResultsData(
        selectedCompany,
        selectedProduct?.id // Pass selected ASIN id
    );

    // Calculate derived percentages
    let percentSalesThisSku: number | null = null;
    let percentSalesOtherSkus: number | null = null;

    const asinRevenue = parseNumericValue(asinData?.lifetimeRevenue);

    if (asinRevenue !== null && companyLifetimeRevenue !== null && companyLifetimeRevenue !== 0) {
        percentSalesThisSku = (asinRevenue / companyLifetimeRevenue); // Keep as decimal for formatting
        percentSalesOtherSkus = 1 - percentSalesThisSku;
    } else if (asinRevenue !== null && companyLifetimeRevenue === 0) {
        percentSalesThisSku = 1; // 100%
        percentSalesOtherSkus = 0; // 0%
    }

    // Define items to display, using correct formatters
    const items = [
        { label: 'Earliest Date in Ledger', value: formatExcelDate(asinData?.earliestDateInLedger) }, // Use new formatter
        { label: 'Days in History', value: formatNumber(asinData?.daysInHistory) },
        { label: 'Lifetime Units', value: formatNumber(asinData?.lifetimeUnits) },
        { label: 'Lifetime Revenue', value: formatCurrency(asinData?.lifetimeRevenue) },
        { label: 'Lifetime PPC Spend', value: formatCurrency(asinData?.lifetimePpcSpend) },
        { label: 'Lifetime Gross Profit', value: formatCurrency(asinData?.lifetimeGrossProfit) },
        // Divider placeholder
        { label: 'divider', value: null },
        { label: 'Company Lifetime Revenue', value: formatCurrency(companyLifetimeRevenue) },
        { label: '% Sales to This SKU', value: formatDecimalAsPercent(percentSalesThisSku) },
        { label: '% Sales to Other SKUs', value: formatDecimalAsPercent(percentSalesOtherSkus) },
    ];


    if (error) {
        return <div className="max-w-xl mx-auto"><Card><CardHeader><CardTitle className="text-2xl font-bold">Lifetime Results</CardTitle></CardHeader><CardContent><p className="text-red-600 text-sm">Error: {error.message}</p></CardContent></Card></div>;
    }

     if (!selectedProduct && !isLoading) {
         return (
            <div className="max-w-xl mx-auto">
                <Card>
                    <CardHeader><CardTitle className="text-2xl font-bold">Lifetime Results</CardTitle></CardHeader>
                    <CardContent><p className="text-sm text-gray-500">Select a product to view Lifetime Results.</p></CardContent>
                </Card>
            </div>
        );
    }

    return (
        <div className="max-w-xl mx-auto"> {/* Constrain width */}
            <Card>
                <CardHeader className="p-4">
                    <CardTitle className="text-2xl font-bold">Lifetime Results</CardTitle>
                </CardHeader>
                <CardContent className="p-4"> {/* Add padding back */}
                    {isLoading ? (
                        // Simple skeleton for this layout
                        <div className="space-y-3">
                            {items.map((item, i) => (
                                item.label === 'divider' ? <hr key={i} className="my-3 border-gray-300 dark:border-gray-600" /> :
                                <div key={i} className="flex justify-between">
                                    <Skeleton className="h-4 w-2/5" />
                                    <Skeleton className="h-4 w-1/4" />
                                </div>
                            ))}
                        </div>
                    ) : !asinData ? (
                         <p className="text-sm text-gray-500 text-center">No lifetime data available for this ASIN.</p>
                    ) : (
                        // Use simple divs for two-column layout
                        <div className="space-y-1"> {/* Reduced space */}
                            {items.map((item, index) => (
                                item.label === 'divider' ? (
                                    <hr key={index} className="my-3 border-gray-300 dark:border-gray-600" />
                                ) : (
                                    // ** Added hover effect and padding/margin for better visual **
                                    <div
                                        key={item.label}
                                        className={cn(
                                            "flex justify-between items-center text-sm",
                                            "px-2 py-1 -mx-2 rounded", // Padding and negative margin for hover area
                                            "hover:bg-gray-100 dark:hover:bg-gray-700" // Hover classes
                                        )}
                                     >
                                        <span className="text-gray-600 dark:text-gray-400">{item.label}</span>
                                        <span className="font-medium text-gray-900 dark:text-gray-100">{item.value}</span>
                                    </div>
                                )
                            ))}
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
};

export default LifetimeResultsDashboard;